import { getDesk, setDesk, type StoredDeskItem } from "./store.ts";

export interface DeskItem {
  ref: string;
  enTitle: string;
  heTitle: string | null;
  category: string;
  addedAt: number;
}

export const DESK_CAP = 4;

type DeskListener = (items: DeskItem[]) => void;

let deskItems: DeskItem[] = [];
const listeners = new Set<DeskListener>();

function clone(item: DeskItem): DeskItem {
  return {
    ref: item.ref,
    enTitle: item.enTitle,
    heTitle: item.heTitle,
    category: item.category,
    addedAt: item.addedAt,
  };
}

function fromStore(item: StoredDeskItem): DeskItem {
  return clone(item);
}

function snapshot(): DeskItem[] {
  return deskItems.map(clone);
}

function persist(): void {
  const items = snapshot();
  void setDesk(items);
}

function emit(): void {
  const items = snapshot();
  for (const listener of listeners) listener(items);
}

function commit(next: DeskItem[]): void {
  deskItems = next.map(clone);
  persist();
  emit();
}

export function getDeskItems(): DeskItem[] {
  return snapshot();
}

export function isOnDesk(ref: string): boolean {
  return deskItems.some((item) => item.ref === ref);
}

export function addToDesk(item: DeskItem): "added" | "exists" | "full" {
  if (isOnDesk(item.ref)) return "exists";
  if (deskItems.length >= DESK_CAP) return "full";
  commit([...deskItems, clone(item)]);
  return "added";
}

export function removeFromDesk(ref: string): void {
  if (!isOnDesk(ref)) return;
  commit(deskItems.filter((item) => item.ref !== ref));
}

export function clearDesk(): void {
  if (!deskItems.length) return;
  commit([]);
}

export function subscribeDesk(listener: DeskListener): () => void {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

export async function loadDeskFromStore(): Promise<void> {
  deskItems = (await getDesk()).map(fromStore);
  emit();
}

export function _resetDeskForTests(): void {
  deskItems = [];
  listeners.clear();
}
